import { useState, type FormEvent, type SyntheticEvent } from "react";
import { useRouter } from "next/router";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import {
  persistProductSessions,
  requestCentralOtp,
  verifyCentralOtp,
} from "src/lib/auth-central/browser";
import { sanitizeInternalReturnTo } from "src/lib/auth-central/unauthorized-session";
import Iconify from "src/components/core/mui/components/Iconify";

import {
  requestRedesignOtp,
  verifyRedesignOtp,
} from "src/lib/redesign-session/browser";
import { redesignReturnTo } from "src/lib/redesign-session/context";

type Step = "request" | "verify";
type LoginMethod = "whatsapp" | "email";

const isWhatsAppOtpEnabled =
  process.env.NEXT_PUBLIC_AUTH_OTP_WHATSAPP_ENABLED === "true";

export default function LoginPage() {
  const router = useRouter();
  const [step, setStep] = useState<Step>("request");
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    isWhatsAppOtpEnabled ? "whatsapp" : "email",
  );
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isRedesign = router.query.experience === "redesign";
  const returnTo = isRedesign
    ? redesignReturnTo(router.query.returnTo)
    : sanitizeInternalReturnTo(
        typeof router.query.returnTo === "string" ? router.query.returnTo : "/",
      );

  async function requestLoginOtp(
    input: Parameters<typeof requestCentralOtp>[0],
  ) {
    if (isRedesign) return requestRedesignOtp(input);
    const response = await requestCentralOtp(input);
    if (response.authenticated) persistProductSessions(response);
    return response;
  }

  async function handleRequestOtp() {
    setIsSubmitting(true);
    setError("");

    try {
      const response = await requestLoginOtp(
        loginMethod === "whatsapp"
          ? {
              phoneNumber: onlyDigits(phoneNumber),
              preferredChannel: "whatsapp",
            }
          : {
              email,
              preferredChannel: "email",
            },
      );
      if (response.authenticated) {
        await router.replace(returnTo);
        return;
      }

      setChallengeId(response.challengeId);
      setStep("verify");
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Nao foi possivel enviar o código.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleVerifyOtp() {
    setIsSubmitting(true);
    setError("");

    try {
      await (isRedesign ? verifyRedesignOtp : verifyCentralOtp)({
        challengeId,
        code,
      });
      router.replace(returnTo);
    } catch (verifyError) {
      setError(
        verifyError instanceof Error ? verifyError.message : "código invalido.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleRequestOtpSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting || !canRequestCode) {
      return;
    }

    handleRequestOtp();
  }

  function handleVerifyOtpSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting || code.length < 6) {
      return;
    }

    handleVerifyOtp();
  }

  function handleMethodChange(_event: SyntheticEvent, value: LoginMethod) {
    if (value === "whatsapp" && !isWhatsAppOtpEnabled) {
      return;
    }

    setLoginMethod(value);
    setError("");
  }

  function handleChangeContact() {
    setStep("request");
    setCode("");
    setChallengeId("");
    setError("");
  }

  const maskedPhone = formatPhoneNumber(phoneNumber);
  const canRequestCode =
    loginMethod === "whatsapp" ? onlyDigits(phoneNumber).length >= 10 : !!email;
  const contactLabel = loginMethod === "whatsapp" ? maskedPhone : email;

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "#f5f7fb",
        backgroundImage:
          "linear-gradient(135deg, rgba(255, 193, 7, 0.13) 0%, rgba(255, 193, 7, 0) 34%), linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(245, 247, 251, 0.96) 100%)",
        display: "flex",
        alignItems: "center",
        py: { xs: 3, sm: 5 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: { xs: 3, sm: 4 },
            border: "1px solid",
            borderColor: "rgba(17, 24, 39, 0.08)",
            boxShadow: "0 24px 80px rgba(15, 23, 42, 0.12)",
            bgcolor: "rgba(255, 255, 255, 0.94)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.88fr 1.12fr" },
              minHeight: { md: 520 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
                bgcolor: "#111827",
                color: "common.white",
                p: { xs: 3, sm: 4 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 4,
                "&:before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  backgroundImage:
                    "linear-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                  maskImage:
                    "linear-gradient(130deg, rgba(0,0,0,0.72), rgba(0,0,0,0.08))",
                },
              }}
            >
              <Stack spacing={1.5} sx={{ position: "relative" }}>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: 34, sm: 42 },
                    fontWeight: 800,
                    letterSpacing: 0,
                    lineHeight: 1,
                  }}
                >
                  Sync
                </Typography>
                <Typography
                  sx={{
                    maxWidth: 280,
                    color: "rgba(255, 255, 255, 0.72)",
                    fontSize: 15,
                    lineHeight: 1.7,
                  }}
                >
                  Uma solução em que você pode confiar.
                </Typography>
              </Stack>

              <Stack
                spacing={1.25}
                sx={{
                  position: "relative",
                  display: { xs: "none", md: "flex" },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 3,
                    borderRadius: 999,
                    bgcolor: "warning.main",
                  }}
                />
                <Typography
                  sx={{
                    color: "rgba(255, 255, 255, 0.56)",
                    fontSize: 13,
                    lineHeight: 1.7,
                  }}
                >
                  Caso você ainda não possua conta na Sync,{" "}
                  <a
                    href="https://wa.me/551145212145"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    clique aqui
                  </a>{" "}
                  para falar com nossa equipe de vendas, ou ligue para{" "}
                  <a href="tel:551145212145"> (11) 4521-2145</a>.
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                p: { xs: 3, sm: 5, md: 6 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <Stack spacing={3} sx={{ width: "100%" }}>
                <Box>
                  <Typography
                    component="h2"
                    sx={{
                      color: "#111827",
                      fontSize: { xs: 24, sm: 28 },
                      fontWeight: 800,
                      letterSpacing: 0,
                      lineHeight: 1.18,
                    }}
                  >
                    {step === "request"
                      ? "Acesse sua conta"
                      : "Confirme seu código"}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ mt: 0.75, fontSize: 14.5, lineHeight: 1.65 }}
                  >
                    {step === "request"
                      ? "Informe o e-mail cadastrado para receber sua senha de acesso."
                      : `Digite a senha enviada para ${contactLabel}.`}
                  </Typography>
                </Box>

                {error ? (
                  <Alert
                    severity="error"
                    sx={{
                      borderRadius: 2,
                      alignItems: "center",
                    }}
                  >
                    {error}
                  </Alert>
                ) : null}

                {step === "request" ? (
                  <Stack
                    component="form"
                    noValidate
                    onSubmit={handleRequestOtpSubmit}
                    spacing={2.25}
                  >
                    {isWhatsAppOtpEnabled ? (
                      <Tabs
                        onChange={handleMethodChange}
                        value={loginMethod}
                        variant="fullWidth"
                        sx={{
                          minHeight: 44,
                          borderRadius: 2,
                          bgcolor: "#f3f4f6",
                          p: 0.5,
                          "& .MuiTabs-indicator": { display: "none" },
                          "& .MuiTab-root": {
                            minHeight: 36,
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontWeight: 700,
                            color: "text.secondary",
                          },
                          "& .Mui-selected": {
                            bgcolor: "common.white",
                            color: "#111827",
                            boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
                          },
                        }}
                      >
                        <Tab label="WhatsApp" value="whatsapp" />
                        <Tab label="E-mail" value="email" />
                      </Tabs>
                    ) : null}

                    {loginMethod === "whatsapp" ? (
                      <TextField
                        autoFocus
                        fullWidth
                        helperText="Enviaremos o código para seu WhatsApp."
                        inputProps={{ inputMode: "numeric" }}
                        label="WhatsApp"
                        value={maskedPhone}
                        onChange={(event) => setPhoneNumber(event.target.value)}
                        placeholder="(11) 99999-9999"
                        sx={inputSx}
                      />
                    ) : (
                      <TextField
                        autoFocus
                        fullWidth
                        helperText="Enviaremos o código para seu e-mail."
                        label="E-mail"
                        type="email"
                        color="warning"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="voce@empresa.com.br"
                        sx={inputSx}
                      />
                    )}
                    <Button
                      data-testid="request-otp"
                      disabled={isSubmitting || !canRequestCode}
                      type="submit"
                      size="large"
                      variant="contained"
                      color="warning"
                      sx={primaryButtonSx}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : (
                          <Iconify
                            sx={{ width: 15, height: 15 }}
                            icon={"fa:envelope"}
                          />
                        )
                      }
                    >
                      {isSubmitting ? "Enviando código" : "Enviar código"}
                    </Button>
                  </Stack>
                ) : (
                  <Stack
                    component="form"
                    noValidate
                    onSubmit={handleVerifyOtpSubmit}
                    spacing={2.25}
                  >
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: 2,
                        bgcolor: "rgba(2, 132, 199, 0.1)",
                        color: "#0f172a",
                        "& .MuiAlert-icon": { color: "#0284c7" },
                      }}
                    >
                      {loginMethod === "whatsapp"
                        ? `Se houver uma conta para o WhatsApp ${maskedPhone}, uma senha de acesso única foi enviada. Se não chegou, confira se o número informado está correto.`
                        : `Se houver uma conta para ${email}, uma senha de acesso única foi enviada. Se não chegou, confira se o e-mail informado está correto.`}
                    </Alert>
                    <TextField
                      autoFocus
                      fullWidth
                      label="Código"
                      inputProps={{
                        inputMode: "numeric",
                        maxLength: 6,
                        sx: {
                          fontSize: 24,
                          fontWeight: 800,
                          letterSpacing: 5,
                          fontVariantNumeric: "tabular-nums",
                        },
                      }}
                      value={code}
                      onChange={(event) =>
                        setCode(onlyDigits(event.target.value))
                      }
                      sx={inputSx}
                    />
                    <Button
                      data-testid="verify-otp"
                      disabled={isSubmitting || code.length < 6}
                      color="warning"
                      size="large"
                      type="submit"
                      variant="contained"
                      sx={primaryButtonSx}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : undefined
                      }
                    >
                      {isSubmitting ? "Verificando" : "Entrar"}
                    </Button>
                    <Button
                      disabled={isSubmitting}
                      onClick={handleChangeContact}
                      sx={{
                        alignSelf: "center",
                        color: "#475569",
                        fontWeight: 700,
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: "rgba(15, 23, 42, 0.06)",
                        },
                      }}
                    >
                      Alterar contato
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: "#ffffff",
    transition: "box-shadow 180ms ease, border-color 180ms ease",
    "& fieldset": {
      borderColor: "rgba(15, 23, 42, 0.16)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(15, 23, 42, 0.34)",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(245, 158, 11, 0.14)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f59e0b",
    },
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 1,
    color: "#64748b",
  },
};

const primaryButtonSx = {
  minWidth: "100%",
  minHeight: 48,
  borderRadius: 2,
  color: "#111827",
  fontWeight: 800,
  textTransform: "none",
  boxShadow: "0 14px 28px rgba(245, 158, 11, 0.22)",
  transition:
    "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
  "&:hover": {
    boxShadow: "0 16px 34px rgba(245, 158, 11, 0.3)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(1px)",
  },
  "&.Mui-disabled": {
    bgcolor: "rgba(148, 163, 184, 0.22)",
    color: "rgba(15, 23, 42, 0.42)",
    boxShadow: "none",
  },
};

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function formatPhoneNumber(value: string) {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }

  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
